import { Card, Text, TextField, Title2, WasabiButton } from '@kk/ui';
import { useState } from 'react';

import { useContentCan } from '@/auth/SessionProvider';
import { LinkButton, Status } from '@/components/bits';
import { deck } from '@/copy/deck';
import { placeholders } from '@/copy/placeholders';
import { useClubBillingReport, useErrorMessage, useSetClubBillingTerms } from '@/data/hooks';
import { monthDay } from '@/lib/time';
import type { ClubBillingReportRow } from '@/types/wire';

const c = deck.clubBilling;
const dollars = (cents: number) => `$${(cents / 100).toFixed(2).replace(/\.00$/, '')}`;

/**
 * One club's month, and the two things an owner sets on it: Comp (never charged) and a sponsor's share (those
 * children are never billed to the club). Either one also unpauses a paused club, which the API does; this row only
 * shows what came back.
 */
function ClubRow({ row }: { row: ClubBillingReportRow }) {
  const errorMessage = useErrorMessage();
  const set = useSetClubBillingTerms();
  const [sponsored, setSponsored] = useState(String(row.sponsoredChildren));
  const [sponsor, setSponsor] = useState(row.sponsor ?? '');
  const [note, setNote] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const dirty = sponsored !== String(row.sponsoredChildren) || sponsor !== (row.sponsor ?? '');

  const send = async (input: Parameters<typeof set.mutateAsync>[0]['input']) => {
    setError(null);
    setNote(null);
    try {
      await set.mutateAsync({ orgId: row.orgId, input });
      setNote(c.saved);
    } catch (e) {
      setError(errorMessage(e));
    }
  };
  const n = Number(sponsored);
  const validCount = sponsored.trim() !== '' && Number.isInteger(n) && n >= 0;

  return (
    <Card variant="quiet" radius="row" padding={18}>
      <div className="stack" data-testid={`club-billing-${row.orgId}`}>
        <Text variant="body" tone="ink">
          {row.name}
        </Text>
        <Text variant="bodySm" tone="muted" testID={`club-billing-status-${row.orgId}`}>
          {[
            row.comped ? c.comped : c.status[row.status],
            row.plan ? c.plan[row.plan] : null,
            row.sponsor,
          ]
            .filter(Boolean)
            .join(' · ')}
        </Text>
        <Text variant="bodySm" tone="muted">
          {c.month(row.highest, row.billable, dollars(row.estimateCents))}
        </Text>
        {row.deletesOn ? (
          <Text variant="bodySm" tone="ink" testID={`club-billing-deletes-${row.orgId}`}>
            {c.deletesOn(`${monthDay(row.deletesOn)} ${new Date(row.deletesOn).getFullYear()}`)}
          </Text>
        ) : null}
        <div className="actions">
          <TextField
            label={c.sponsoredChildren}
            value={sponsored}
            onChangeText={setSponsored}
            testID={`club-billing-sponsored-${row.orgId}`}
          />
          <TextField
            label={c.sponsor}
            value={sponsor}
            onChangeText={setSponsor}
            testID={`club-billing-sponsor-${row.orgId}`}
          />
        </div>
        <div className="actions">
          {dirty ? (
            <WasabiButton
              label={c.save}
              size="sm"
              fullWidth={false}
              disabled={!validCount}
              onPress={() => send({ sponsoredChildren: n, sponsor: sponsor.trim() || null })}
              testID={`club-billing-save-${row.orgId}`}
            />
          ) : null}
          <LinkButton
            label={row.comped ? c.stopComping : c.comp}
            onPress={() => send({ comped: !row.comped })}
            testID={`club-billing-comp-${row.orgId}`}
          />
        </div>
        {note ? (
          <Text variant="bodySm" tone="muted" testID={`club-billing-note-${row.orgId}`}>
            {note}
          </Text>
        ) : null}
        {error ? <Status testID={`club-billing-error-${row.orgId}`}>{error}</Status> : null}
      </div>
    </Card>
  );
}

/**
 * **The admin's Club Billing page** (DECISIONS 92). Owners only, like Applications: every club's month (what it
 * owes, who sponsors it, which are paused and when a paused one is deleted), and per club the Comp switch and the
 * sponsor's share. Card, invoices and tax stay on Stripe's side; nothing here touches money.
 */
export function ClubBillingScreen() {
  const owner = useContentCan('team');
  const report = useClubBillingReport(owner);
  const errorMessage = useErrorMessage();

  if (!owner) return <Status testID="club-billing-owners-only">{c.ownersOnly}</Status>;
  const rows = report.data ?? [];
  return (
    <>
      <div className="page-header">
        <div className="page-header-text">
          <Title2 tone="ink">{c.title}</Title2>
          <Text variant="bodySm" tone="muted">
            {c.subtitle}
          </Text>
        </div>
      </div>
      {report.isPending ? <Status>{placeholders.empty.loading}</Status> : null}
      {report.error ? (
        <Status testID="club-billing-error">{errorMessage(report.error)}</Status>
      ) : null}
      {report.isSuccess && rows.length === 0 ? (
        <Status testID="club-billing-empty">{c.empty}</Status>
      ) : null}
      <div className="stack">
        {rows.map((row) => (
          <ClubRow key={row.orgId} row={row} />
        ))}
      </div>
    </>
  );
}
